import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, MessageCircle, Star, ShoppingBag } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";

const DEFAULT_NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/dealers", label: "Dealers & Resellers" },
  { to: "/corporate", label: "Corporate Supply" },
  { to: "/new-arrivals", label: "New Arrivals" },
  { to: "/deals", label: "Deals" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const s = useSettings();
  const { count } = useEnquiry();
  const [open, setOpen] = useState(false);
  const [NAV, setNav] = useState(DEFAULT_NAV);
  useEffect(() => { api.get("/navigation").then(r => r.data.length && setNav(r.data.map(n => ({ to: n.link, label: n.label, ext: n.new_tab || /^https?:/.test(n.link) })))).catch(() => {}); }, []);
  const phone = s.phone_primary || "9513951318";
  const wa = s.whatsapp_primary || "9513951318";

  return (
    <header className="sticky top-0 z-40 bg-[#0F172A] text-white shadow-md" data-testid="site-header">
      {/* Top strip */}
      <div className="bg-[#0B1220] text-slate-300 text-xs">
        <div className="container-x flex flex-wrap items-center justify-between py-2 gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <a href={`tel:${phone}`} className="hover:text-white flex items-center gap-1" data-testid="topbar-phone"><Phone className="w-3.5 h-3.5"/> {phone}</a>
            <span className="opacity-40">|</span>
            <a href={`mailto:${s.email || "rextechitsolutions@gmail.com"}`} className="hover:text-white hidden sm:inline">{s.email || "rextechitsolutions@gmail.com"}</a>
            <span className="opacity-40 hidden sm:inline">|</span>
            <span className="hidden md:inline">{s.address || "Bengaluru, Karnataka"}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-amber-400"><Star className="w-3.5 h-3.5 fill-amber-400"/> {s.google_rating || 5.0} ({s.google_reviews_count || 12} Google Reviews)</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="container-x flex items-center justify-between gap-4 py-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          {s.logo ? (
            <div className="bg-white rounded-md p-1 shadow-sm">
              <img src={s.logo} alt="REXTECH IT SOLUTIONS" className="h-11 sm:h-12 w-auto object-contain"/>
            </div>
          ) : (
            <div className="w-10 h-10 bg-[#EA580C] rounded-md flex items-center justify-center font-heading font-extrabold text-white text-lg">RX</div>
          )}
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map(n => n.ext ? (
            <a key={n.to} href={n.to} target="_blank" rel="noreferrer" className="px-3 py-2 text-sm font-medium rounded-md hover:bg-white/10 text-white">{n.label}</a>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({isActive}) =>
              `px-3 py-2 text-sm font-medium rounded-md hover:bg-white/10 ${isActive ? "text-[#F97316]" : "text-white"}`
            } data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>{n.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/enquiry" className="relative" data-testid="header-enquiry-list">
            <Button size="sm" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0F172A]">
              <ShoppingBag className="w-4 h-4 mr-1"/> Enquiry {count > 0 && <span className="ml-1 bg-[#EA580C] text-white text-xs rounded-full px-2">{count}</span>}
            </Button>
          </Link>
          <a href={`https://wa.me/91${wa}`} target="_blank" rel="noreferrer" className="hidden sm:block" data-testid="header-whatsapp">
            <Button size="sm" className="bg-[#25D366] hover:bg-[#1EBE5A] text-white"><MessageCircle className="w-4 h-4 mr-1"/>WhatsApp</Button>
          </a>
          <Link to="/request-quote" className="hidden md:block" data-testid="header-request-quote">
            <Button size="sm" className="btn-orange">Request Quote</Button>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="xl:hidden p-2 rounded-md hover:bg-white/10" data-testid="mobile-menu-toggle" aria-label="menu"><Menu className="w-6 h-6"/></button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#0F172A] text-white border-none w-72">
              <div className="flex flex-col gap-1 mt-6">
                {NAV.map(n => n.ext ? (
                  <a key={n.to} href={n.to} target="_blank" rel="noreferrer" className="px-4 py-3 rounded-md hover:bg-white/10 text-white">{n.label}</a>
                ) : (
                  <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                    className={({isActive}) => `px-4 py-3 rounded-md hover:bg-white/10 ${isActive ? "text-[#F97316]" : "text-white"}`}>
                    {n.label}
                  </NavLink>
                ))}
                <Link to="/request-quote" onClick={() => setOpen(false)} className="mt-3">
                  <Button className="btn-orange w-full">Request Quote</Button>
                </Link>
                <a href={`https://wa.me/91${wa}`} target="_blank" rel="noreferrer" className="mt-2">
                  <Button className="bg-[#25D366] w-full text-white"><MessageCircle className="w-4 h-4 mr-1"/> WhatsApp</Button>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
